import { useEffect, useId, useRef, useState } from 'react';
import { API_URL } from '../utils/api';
import { captureAttribution, getAttribution } from '../utils/attribution';
import { getConsentLeadFields } from '../utils/cookieConsent';
import { trackGenerateLead } from '../utils/leadTracking';

const { safeUrl } = require('./catalog.cjs');
const EMPTY = { name: '', email: '', company: '', phone: '', message: '', website_confirm: '' };

export default function LeadFormSection({ section, sectionIndex = 0 }) {
  const uid = useId().replace(/:/g, '');
  const sendingRef = useRef(false);
  const [values, setValues] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const privacy = safeUrl(section.privacy_link || '/integritetspolicy') || '/integritetspolicy';
  const offer = section.offer_label || 'Elidos tjänster';

  useEffect(() => { captureAttribution(); }, []);

  const change = (name) => (event) => setValues(current => ({ ...current, [name]: event.target.value }));

  async function handleSubmit(event) {
    event.preventDefault();
    if (sendingRef.current || submitted) return;
    setError('');
    if (!values.name.trim() || !values.email.trim()) {
      setError('Fyll i ditt namn och din e-postadress.');
      return;
    }
    sendingRef.current = true;
    setSending(true);
    try {
      captureAttribution();
      const slug = window.location.pathname.replace(/^\/+|\/+$/g, '').slice(0, 160);
      const payload = {
        name: values.name.trim(), email: values.email.trim(),
        company: section.show_company ? values.company.trim() : '',
        phone: section.show_phone !== false ? values.phone.trim() : '',
        message: section.show_message !== false && values.message.trim()
          ? values.message.trim() : 'Jag vill få ett förslag på: ' + offer,
        website_confirm: values.website_confirm,
        landing_slug: slug, form_id: 'lead_form_' + sectionIndex,
        form_variant: section.variant, form_offer: offer,
        attribution: getAttribution(), ...getConsentLeadFields(),
      };
      const response = await fetch(API_URL + '/api/contact', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.error || 'Det gick inte att skicka förfrågan. Försök igen.');
      }
      trackGenerateLead({ accepted: result.accepted, lead_id: result.lead_id, form_source: 'cms_landing_form' });
      setSubmitted(true);
      setValues(EMPTY);
    } catch (err) {
      setError(err.message || 'Det gick inte att skicka förfrågan. Försök igen.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  const field = (key, label, type, placeholder, required = false, maxLength = 160) => (
    <label key={key} htmlFor={uid + '-' + key} className="el-form-field">
      <span>{label}{required && <span aria-hidden="true"> *</span>}</span>
      <input name={key} id={uid + '-' + key} type={type} required={required}
        value={values[key]} onChange={change(key)} placeholder={placeholder}
        autoComplete={key} maxLength={maxLength} disabled={sending} />
    </label>
  );

  return (
    <section className={'el-section el-lead-form el-lead-form-' + section.variant}
      data-elido-section="lead_form" data-variant={section.variant} data-testid="cms-lead-form">
      <div className="el-wrap"><div className="el-form-shell">
        <div className="el-form-intro">
          {section.eyebrow && <p className="el-kicker">{section.eyebrow}</p>}
          <h2 className="el-form-title">{section.title || 'Berätta om ditt projekt.'}</h2>
          {section.subtitle && <p className="el-body">{section.subtitle}</p>}
          {offer && <span className="el-form-offer">{offer}</span>}
        </div>
        <div>
          {submitted ? (
            <div className="el-form-state" role="status" data-testid="cms-lead-form-success">
              <strong>Tack för din förfrågan!</strong>
              <span>{section.success_message || 'Vi har tagit emot dina uppgifter och hör av oss.'}</span>
            </div>
          ) : (
            <form className="el-form-card" onSubmit={handleSubmit} data-testid="cms-lead-form-fields">
              <div className="el-form-fields">
                {field('name', 'Ditt namn', 'text', 'För- och efternamn', true, 150)}
                {field('email', 'E-postadress', 'email', 'namn@foretag.se', true, 254)}
                {section.show_company && field('company', 'Företag (valfritt)', 'text', 'Företagets namn')}
                {section.show_phone !== false && field('phone', 'Telefon (valfritt)', 'tel', '070-123 45 67', false, 50)}
                {section.show_message !== false && (
                  <label className="el-form-field el-form-message" htmlFor={uid + '-message'}>
                    <span>Vad behöver du hjälp med? (valfritt)</span>
                    <textarea name="message" id={uid + '-message'} rows={3} maxLength={2500}
                      placeholder="Berätta kort om ditt företag eller projekt..." value={values.message}
                      onChange={change('message')} disabled={sending} />
                  </label>
                )}
              </div>
              <label className="el-form-honeypot" aria-hidden="true">Lämna detta fält tomt
                <input name="website_confirm" type="text" tabIndex={-1} autoComplete="off"
                  value={values.website_confirm} onChange={change('website_confirm')} />
              </label>
              <button className="el-form-submit" type="submit" disabled={sending} data-testid="cms-lead-form-submit">
                {sending ? 'Skickar...' : (section.button_text || 'Få ett kostnadsfritt förslag')}
                <span aria-hidden="true">↗</span>
              </button>
              {error && <p className="el-form-error" role="alert">{error}</p>}
              <p className="el-form-privacy">Inga förpliktelser. Läs vår <a href={privacy}>integritetspolicy</a>.</p>
            </form>
          )}
        </div>
      </div></div>
    </section>
  );
}
